import type { ActiveTurn, SliceCreator, TurnActions } from './types';

const sameTurn = (a: ActiveTurn, b: ActiveTurn): boolean =>
  a.turnId === b.turnId &&
  a.identityMode === b.identityMode &&
  a.workflowId === b.workflowId &&
  a.runId === b.runId &&
  a.phase === b.phase &&
  a.startedAt === b.startedAt &&
  a.updatedAt === b.updatedAt &&
  a.authoritativeCompletionWorkflowId === b.authoritativeCompletionWorkflowId;

export const createTurnsSlice: SliceCreator<TurnActions> = (set) => ({
  setActiveSubmission: (sessionId, submission) =>
    set((s) => ({
      activeSubmissionBySession: {
        ...s.activeSubmissionBySession,
        [sessionId]: submission,
      },
    })),

  clearActiveSubmission: (sessionId) =>
    set((s) => {
      if (!(sessionId in s.activeSubmissionBySession)) return s;
      const submissions = { ...s.activeSubmissionBySession };
      delete submissions[sessionId];
      return { activeSubmissionBySession: submissions };
    }),

  upsertActiveTurn: (sessionId, patch = {}) =>
    set((s) => {
      const previous = s.activeTurnsBySession[sessionId];
      const next: ActiveTurn = {
        phase: 'running',
        startedAt: previous?.startedAt ?? new Date().toISOString(),
        ...previous,
        ...patch,
      };
      const tabsAlreadyStreaming = s.openTabs.every(
        (tab) => tab.sessionId !== sessionId || tab.status === 'streaming'
      );
      if (previous && sameTurn(previous, next) && tabsAlreadyStreaming)
        return s;
      return {
        activeTurnsBySession: {
          ...s.activeTurnsBySession,
          [sessionId]: next,
        },
        openTabs: s.openTabs.map((tab) =>
          tab.sessionId === sessionId
            ? { ...tab, status: 'streaming' as const }
            : tab
        ),
      };
    }),

  resolveActiveTurn: (sessionId, status) =>
    set((s) => {
      const completedTurnId = s.activeTurnsBySession[sessionId]?.turnId;
      const activeTurns = { ...s.activeTurnsBySession };
      delete activeTurns[sessionId];
      const uploadProgress = { ...s.uploadProgressBySession };
      // WebSocket/status completion can beat the original POST response. In
      // that ordering, waiting for the request's finally block leaves a stale
      // "Uploading…" strip behind (potentially forever if the response was
      // lost). Clear only the progress owned by the turn being resolved so a
      // newer upload in the same session cannot be erased.
      if (
        completedTurnId &&
        uploadProgress[sessionId]?.turnId === completedTurnId
      ) {
        delete uploadProgress[sessionId];
      }
      const activeSubmissions = { ...s.activeSubmissionBySession };
      // A failed turn keeps its immutable input so the error strip can retry
      // the exact text/files/selection. Successful completion no longer needs
      // the browser-only snapshot.
      if (status === 'done') delete activeSubmissions[sessionId];
      const queued = { ...s.queuedBySession };
      if (status === 'done' && completedTurnId && queued[sessionId]) {
        const remaining = queued[sessionId].filter(
          (turn) => turn.id !== completedTurnId
        );
        if (remaining.length > 0) queued[sessionId] = remaining;
        else delete queued[sessionId];
      }
      return {
        activeTurnsBySession: activeTurns,
        activeSubmissionBySession: activeSubmissions,
        uploadProgressBySession: uploadProgress,
        queuedBySession: queued,
        openTabs: s.openTabs.map((tab) =>
          tab.sessionId === sessionId ? { ...tab, status } : tab
        ),
      };
    }),
});
